import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { api, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { Campo } from '@/components/Campo';
import { Modal } from '@/components/Modal';

import { SelectorEspecialidad } from './comun/SelectorEspecialidad';
import { rutaCompuesto } from './tipos';

interface Alta {
  titulo: string;
  nombreMax: number;
  especialidadRequerida?: boolean;
  crear: (body: { name: string; specialtyId: number | null }) => Promise<{ id: number }>;
}

/** Alta corta de los tipos con pantalla propia: nombre y especialidad, y luego su editor. */
const ALTAS: Record<string, Alta> = {
  clase: {
    titulo: 'Nueva clase',
    nombreMax: 120,
    especialidadRequerida: true,
    crear: ({ name, specialtyId }) =>
      unwrap(api.POST('/api/v1/admin/classes', { body: { name, specialtyId: specialtyId ?? 0 } })),
  },
  presentacion: {
    titulo: 'Nueva presentación',
    nombreMax: 100,
    crear: (body) => unwrap(api.POST('/api/v1/admin/presentations', { body })),
  },
  resumengpc: {
    titulo: 'Nuevo resumen GPC',
    nombreMax: 250,
    crear: (body) => unwrap(api.POST('/api/v1/admin/gpc-summaries', { body })),
  },
};

export function NuevoCompuesto({ tipo, onCerrar }: { tipo: string | null; onCerrar: () => void }) {
  const alta = tipo ? ALTAS[tipo] : undefined;
  return (
    <Modal abierto={alta !== undefined} onCerrar={onCerrar} titulo={alta?.titulo ?? ''}>
      {tipo && alta && <Formulario key={tipo} tipo={tipo} alta={alta} onCerrar={onCerrar} />}
    </Modal>
  );
}

function Formulario({ tipo, alta, onCerrar }: { tipo: string; alta: Alta; onCerrar: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [specialtyId, setSpecialtyId] = useState<number | null>(null);
  const crear = useMutation({
    mutationFn: () => alta.crear({ name: name.trim(), specialtyId }),
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: ['contenido'] });
      toast.success('Creado. Completa el resto en su pantalla.');
      onCerrar();
      const ruta = rutaCompuesto(tipo, d.id);
      if (ruta) void navigate(ruta);
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        crear.mutate();
      }}
    >
      <Campo etiqueta="Nombre" ayuda={`Hasta ${alta.nombreMax} caracteres.`}>
        <input
          className="input-vidrio"
          value={name}
          required
          minLength={2}
          maxLength={alta.nombreMax}
          autoFocus
          onChange={(e) => setName(e.target.value)}
        />
      </Campo>
      <Campo etiqueta={alta.especialidadRequerida ? 'Especialidad *' : 'Especialidad'}>
        <SelectorEspecialidad
          valor={specialtyId}
          requerido={alta.especialidadRequerida}
          vacio={alta.especialidadRequerida ? 'Elige una especialidad' : 'Sin especialidad'}
          onCambio={setSpecialtyId}
        />
      </Campo>
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-cs btn-contorno" onClick={onCerrar}>
          Cancelar
        </button>
        <button type="submit" className="btn-cs btn-primario" disabled={crear.isPending}>
          Crear y abrir
        </button>
      </div>
    </form>
  );
}
